<script setup>
import { ref, onMounted, computed } from "vue";
import { useRouter } from "vue-router";
import api from "@/services/apiService";

import ArgonAlert from "@/components/ArgonAlert.vue";
import BackendPagination from "@/components/BackendPagination.vue";
import ArgonSwitch from "@/components/ArgonSwitch.vue";

// State
const subscribers = ref([]);
const errorMessage = ref("");
const successMessage = ref("");
const loading = ref(true);

// Pagination
const currentPage = ref(1);
const itemsPerPage = 10;
const totalItems = ref(0);

const router = useRouter();
const totalPages = computed(() => Math.ceil(totalItems.value / itemsPerPage));

// Fetch subscribers
const fetchSubscribers = async () => {
    loading.value = true;
    try {
        const response = await api.get(`/subscribers?page=${currentPage.value}`);
        subscribers.value = response.data.member || [];
        totalItems.value = response.data.totalItems || 0;
    } catch (error) {
        errorMessage.value = error.response?.data?.message || "Error al obtener suscriptores.";
    } finally {
        loading.value = false;
    }
};

// Pagination handler
const changePage = async (page) => {
    if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
        await fetchSubscribers();
    }
};

// Toggle Active
const toggleStatus = async (subscriber) => {
    try {
        const id = subscriber["@id"].replace(/^\/api/, "");
        const newStatus = subscriber.status === "active" ? "inactive" : "active";

        await api.patch(id, { status: newStatus });
        subscriber.status = newStatus;
    } catch (error) {
        errorMessage.value = "No se pudo actualizar el estado del suscriptor.";
        setTimeout(() => (errorMessage.value = ""), 3000);
    }
};


// Delete
const deleteSubscriber = async (subscriber) => {
    if (!confirm(`¿Eliminar el suscriptor "${subscriber.email}"?`)) return;

    try {
        const id = subscriber["@id"].replace(/^\/api/, "");
        await api.delete(id);
        fetchSubscribers();

        successMessage.value = `Suscriptor "${subscriber.email}" eliminado correctamente.`;
        setTimeout(() => (successMessage.value = ""), 3000);
    } catch (error) {
        errorMessage.value = error.response?.data?.message || "Error al eliminar el suscriptor.";
        setTimeout(() => (errorMessage.value = ""), 3000);
    }
};

// Init
onMounted(() => {
    const token = localStorage.getItem("auth_token");
    if (!token) {
        router.push("/signin");
    } else {
        fetchSubscribers();
    }
});
</script>

<template>
    <div class="py-4 container-fluid">
        <div class="row">
            <div class="col-12">
                <div class="card">
                    <div class="card-header pb-0">
                        <h6>Suscriptores</h6>
                    </div>

                    <div class="card-body px-0 pt-0 pb-2">
                        <div class="table-responsive p-0">
                            <div v-if="loading" class="text-center py-3">
                                <p class="text-secondary">Cargando suscriptores...</p>
                            </div>

                            <argon-alert v-if="successMessage" color="alert-success" icon="ni ni-check-bold"
                                class="mb-2 text-dark w-50 ms-3">
                                {{ successMessage }}
                            </argon-alert>

                            <argon-alert v-if="errorMessage" color="alert-danger" icon="ni ni-fat-remove"
                                class="mb-2 text-dark w-50 ms-3">
                                {{ errorMessage }}
                            </argon-alert>

                            <div v-if="subscribers.length === 0 && !loading && !errorMessage" class="text-center py-3">
                                <img src="@/assets/img/illustrations/noData.png" alt="No hay datos disponibles"
                                    width="300" />
                                <p class="text-secondary">No se encontraron suscriptores</p>
                            </div>

                            <table v-if="subscribers.length > 0" class="table align-items-center mb-0">
                                <thead>
                                    <tr>
                                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                                            Email</th>
                                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">
                                            Activo</th>
                                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="subscriber in subscribers" :key="subscriber['@id']">
                                        <td>
                                            <div class="d-flex px-2 py-1">
                                                <div class="d-flex flex-column justify-content-center">
                                                    <h6 class="mb-0 text-sm">{{ subscriber.email }}</h6>
                                                </div>
                                            </div>
                                        </td>
                                        <td>

                                            <argon-switch :id="'subscriber-' + subscriber.id"
                                                :name="'subscriber-' + subscriber.id"
                                                :checked="subscriber.status === 'active'"
                                                @change="toggleStatus(subscriber)" />
                                        </td>


                                        <td>
                                            <font-awesome-icon :icon="['fas', 'trash']"
                                                @click="deleteSubscriber(subscriber)" style="cursor:pointer;" />
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            <BackendPagination :current-page="currentPage" :total-pages="totalPages"
                                @change-page="changePage" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>


<style scoped>
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1050;
}

.modal-content {
    background: white;
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0px 5px 15px rgba(0, 0, 0, 0.3);
    z-index: 1051;
    width: 500px;
    max-width: 90%;
    position: relative;
}
</style>
